import { updater, responseUpdater } from "../../utils/updater.js";
import logger from "../../utils/logger.js";
import apiRequest from "../../utils/ApiRequest/apiRequest.js";
import parse from "html-react-parser";
import config from "config";
import { setLocalStorage } from "utils/globalLocalStorage.js";
import Cookies from "universal-cookie";
import isInIframe from "utils/detectIframe.ts";

const staticImage = `${config.api.resource}/assets/images/backgrounds/defaultGalleryBackgroundCover2.webp`;
const isDev =
  process.env.REACT_APP_STAGE && process.env.REACT_APP_STAGE.trim() !== "prod";

const cookies = new Cookies();
// Available languages
export const languages = {
  en: { text: "English", culture: "en-US" },
  he: { text: "עברית", culture: "he-IL" },
  ru: { text: "русский", culture: "ru-RU" },
  it: { text: "italiano", culture: "it-IT" },
  es: { text: "español", culture: "es-ES" },
  ar: { text: "عربى", culture: "ar-SA" },
};
const MOBILE_MAX_WIDTH = 900;

/**
 * language API factory.
 * has the capabilities to get and set the language.
 *
 * @param {Object} obj - given to the function by the 'useApi' hook
 * @param {Object} obj.state - the state of a language context
 * @param {String} obj.state.lang - current language
 * @param {Function} obj.setState - a setting function
 */
export function ContentFactory({ state, setState }) {
  // shorthand for setState(produce(draft => {...}))
  const updateState = updater(setState);

  /**
   * changes the current lang if its one of the supported langs
   * and sets the new lang in the localStorage
   * @param {String} lang - the language to change to, make sure it's supported
   */
  const setLanguage = (lang) => {
    if (lang) {
      if (lang === state.lang) return;
      if (Object.keys(languages).includes(lang))
        updateState((draft) => {
          draft.direction = ["he", "ar"].includes(lang) ? "rtl" : "ltr";
          draft.lang = lang;
          setLocalStorage("lang", lang);
        });
      // if we reached here, someone gave this function a bad string
      else throw new Error(`${lang} is unrecognized!`);
    }
  };

  /**
   * helper function for easier reading.
   * concat the current language to the pathname
   * @param {Object} obj
   */
  const concatPath = (obj) => ({
    ...obj,
    pathname: `/${state.lang}${obj.pathname}`,
  });
  //getters
  const getLanguage = () => state.lang;
  const getLanguageCulture = () => languages[getLanguage()].culture;
  const getTextCategories = () => state.textCategories;
  const getDirection = () => state.direction;
  const getLanguages = () => languages;
  const getMetaData = () => state.pageMetaData;
  const getPageText = () => state.pageText;
  const getAllTextData = () => state.constText;
  const getCurrentPage = () =>
    state.currentPage ? state.currentPage.toLowerCase() : null;
  const isBasicSettingsInitialized = () => state.basicSettings.isInit;
  const setIsGoogleAnalyticsInitialized = (value) => {
    updateState((draft) => {
      draft.basicSettings = { ...draft.basicSettings, isGAInit: value };
    });
  };
  const isGoogleAnalyticsInitialized = () => {};
  const getAppVersion = () => state.appVersion;
  const getBasicSettingsData = () => state.basicSettings;
  const getDefaultBackgroundImage = () => {
    const isMobile = window.innerWidth < MOBILE_MAX_WIDTH;
    return isMobile
      ? state.defaultBackgroundImageMobile
      : state.defaultBackgroundImage;
  };
  const getAnnouncement = () => state.announcement;
  const getRandBackgroundFileType = () => state.fileType;
  const getRandBackgroundRedirectUrl = () => state.redirectUrl;

  const fetchDefaultBackgroundImage = async ({
    location,
    fileType = "webp",
    campaignIds = [], // Campaign IDs to exclude from fetching
  }) => {
    if (window.location.pathname.includes("/preview-ad-")) {
      return;
    }

    const encryptedMailId =
      location === "DOWNLOAD"
        ? (window.location.pathname.match(/\/(gallery|collection)\/([^\/]+)/) ||
            [])[2]
        : undefined;

    const isCollection =
      encryptedMailId && window.location.pathname.includes("/collection/");

    const response = await apiRequest(
      "Background",
      "GetRandBackground",
      {
        fileTypePrefferd: fileType,
        displayLocation: location,
        language: getLanguage(),
        encryptedMailId,
        country: null,
        isDesktop: window.innerWidth > 768,
        campaignIds,
        isCollection,
      },
      "POST"
    );
    // Put it here for test purposes (when need to show ad script locally)
    // response.data = {
    //   fileLocation:
    //     "https://resources.jumbomail.me/ad-photo/adphoto_d70bc500-1cb9-4cb8-a75f-efa470bc3dc0.webp?X-Amz-Expires=88933&response-cache-control=no-cache&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=AKIAIFY6RTLT2GCF26QA/20241009/eu-west-1/s3/aws4_request&X-Amz-Date=20241009T121747Z&X-Amz-SignedHeaders=host&X-Amz-Signature=03ed24f2ed27a787357d25627b02ad429a9e0a2182a8f955f3af7e99312a75ca",
    //   fileMobileLocation:
    //     "https://resources.jumbomail.me/ad-mobile-photo/adthumbphoto_12802c0d-ef29-4956-88bb-f673dbb01ba2.webp?X-Amz-Expires=88933&response-cache-control=no-cache&X-Amz-Algorithm=AWS4-HMAC-SHA256&X-Amz-Credential=AKIAIFY6RTLT2GCF26QA/20241009/eu-west-1/s3/aws4_request&X-Amz-Date=20241009T121747Z&X-Amz-SignedHeaders=host&X-Amz-Signature=a7cf2bb4f4892cbb38b18b3158e80c1aaf98defdad32177d145f11d8ffaee95b",
    //   fileType: "Image",
    //   description: "Secon ad image",
    //   redirectUrl: "https://dicentra.ua/korobka-51-eustoma-miks?pack=pink",
    //   artist: null,
    //   script:
    //     "https://ad.doubleclick.net/ddm/adj/N1710302.279382DBMTP-MADGROWTH-X/B32195485.399265028;dcopt=1_ist;dc_sdk_apis=[APIFRAMEWORKS];dc_omid_p=[OMIDPARTNER];gdpr=$%7BGDPR%7D;gdpr_consent=$%7BGDPR_CONSENT_755%7D;dc_tdv=1;sz=0x0;ord=[timestamp];dc_lat=;dc_rdid=;tag_for_child_directed_treatment=;tfua=&v=638640838671585591",
    //   adId: 15,
    // };
    if (response.status === "success") {
      if (response.data?.adId > 0) {
        updateState((draft) => {
          draft.adsBackgorund = response.data;
        });
      }
      if (!response.data?.fileLocation) return;
      updateState((draft) => {
        draft.defaultBackgroundImage = response.data.fileLocation;
        draft.defaultBackgroundImageMobile =
          response.data.fileMobileLocation || response.data.fileLocation;
        draft.artist = response.data.artist;
        draft.fileType = response.data.fileType;
        draft.redirectUrl = response.data.redirectUrl
          ? response.data.redirectUrl.trim()
          : undefined;
        draft.uniqueViews = response.data?.uniqueViews;
      });
    } else {
      updateState((draft) => {
        draft.defaultBackgroundImage = staticImage;
      });
      logger("contentFactory", "GetRandBackground", response.data);
    }
  };

  const fetchAdsBackground = async () => {
    const adId = window.location.pathname.split("/preview-ad-")[1];

    const response = await apiRequest("Background", "GetPreviewBackground", {
      adId,
    });

    const payload = { ...response.data };

    if (
      window.location.pathname.includes("/preview-ad-") &&
      payload.fileLocation
    ) {
      payload.script = "";
      updateState((draft) => {
        draft.defaultBackgroundImage = response.data.fileLocation;
        draft.defaultBackgroundImageMobile =
          response.data.fileMobileLocation || response.data.fileLocation;
      });
    }

    if (response.status === "success") {
      updateState((draft) => {
        draft.adsBackgorund = payload;
      });
    } else {
      logger("contentFactory", "GetPreviewBackground", response.data);
    }
  };

  const setAdsBackground = (data) => {
    updateState((draft) => {
      draft.adsBackgorund = data;
    });
  };

  const getAdsBackground = () => state.adsBackgorund;

  /**
   * replaces keywords in text.
   * @param {String} text - the text to manipulate
   * @param {Array<String>} keywords - keywords exists in the text (i.e #BaseUrl#). if null, it will use default array of keywords.
   * @param {Array<String>} replaceWith - words to replace the keywords with. order of the index does matter.
   * @returns {String} - a text (after manipulation) to show to user
   *    */

  const replaceKeywordsInText = (text, keywords, replaceWith) => {
    if (!text) return;
    let result = text;
    //if keywords array is empty, use default array.
    if (!keywords) {
      keywords = ["#BaseUrl#", "#SupportMail#"];
    }
    if (typeof text !== "string") {
      const objResult = text.map((item) => {
        if (typeof item === "string") {
          if (item.indexOf("#") > 0) {
            return keywords.forEach((keyword, i) => {
              const regex = new RegExp(`{${keyword}}`, "g");
              // replacing the keyword. if replaceWith is not provided, take from app config
              return item.replace(
                regex,
                replaceWith[i]
                  ? replaceWith[i]
                  : config[keyword.replace(/#/g, "").toLowerCase()] || ""
              );
            });
          } else {
            return item;
          }
        } else {
          const propsObj = {};
          keywords.forEach((keyword, i) => {
            const regex = new RegExp(`${keyword}`, "g");
            // replacing the keyword. if replaceWith is not provided, take from app config
            Object.entries(item.props).forEach(([key, val]) => {
              propsObj[key] = val?.replace(
                regex,
                replaceWith[i]
                  ? replaceWith[i]
                  : config[keyword.replace(/#/g, "").toLowerCase()] || ""
              );
            });
          });
          return { ...item, props: propsObj };
        }
      });
      return objResult;
    }

    if (typeof text === "string") {
      let languageUrl = getLanguage();
      keywords.forEach((keyword, i) => {
        if (text.includes(keyword)) {
          if (keyword === "#BaseUrl#") {
            let base = window.location.href.split("/" + languageUrl)[0];
            text = text.replaceAll(keyword, base.toString());
          }
        }
      });
    }
    result = text;
    keywords.forEach((keyword, i) => {
      const regex = new RegExp(`{${keyword}}`, "g");
      // replacing the keyword. if replaceWith is not provided, take from app config
      result = result.replace(
        regex,
        replaceWith && replaceWith[i]
          ? replaceWith[i]
          : config[keyword.replace(/#/g, "").toLowerCase()] || ""
      );
    });
    return result;
  };

  /**
   * initializing the basic settings
   **/
  const initBasicSettings = async () => {
    const response = await apiRequest("Utils", "ReactGetBasicSettings");
    if (response.status === "success") {
      // check if LastReleaseDate exist in local storage, if not - set it,
      const apiLastReleaseDate = response.data.LastReleaseDate;

      const STORAGE_KEY = "LastReleaseDate";

      // for dev purposes we need to set the recaptcha type to google, cos the recaptcha is not working on the local host
      const isDev = process.env.REACT_APP_STAGE?.trim() === "dev";
      const globalCaptchaType = isDev ? "google" : response.data.recaptchaType;

      if (globalCaptchaType) {
        localStorage.setItem("globalCaptchaType", globalCaptchaType);
      }

      if (apiLastReleaseDate) {
        if (!localStorage.getItem(STORAGE_KEY)) {
          localStorage.setItem(STORAGE_KEY, apiLastReleaseDate);
        } else {
          // otherwise - check if it's different from the one in local storage
          // and reload the page and set the new date in local storage
          if (localStorage.getItem(STORAGE_KEY) !== apiLastReleaseDate) {
            localStorage.setItem(STORAGE_KEY, apiLastReleaseDate);
            window.location.reload(true);
          }
        }
      }

      response.data.isInit = true;

      updateState((draft) => {
        draft.basicSettings = response.data;
      });

      if (isDev) {
        updateState((draft) => {
          draft.basicSettings.recaptchaType = "google";
        });
      }

      return response.data ? response.data.userVideoToken : null;
    } else {
      if (
        response.data === "access-forbidden" &&
        !window.location.pathname.includes("/forbidden")
      ) {
        window.location.href = config.baseurl + getPath() + "forbidden";
      }
      updateState((draft) => {
        draft.isInit = false;
      });
    }
  };

  const INDEX_WITHOUT_L_PREFIX = -1;

  //getters:
  const getCurrentPageType = () => {
    const path = window.location.pathname.split("/");
    const thirdPart = 3 + INDEX_WITHOUT_L_PREFIX;

    return path[thirdPart] && path[thirdPart] === "gallery"
      ? "gallery"
      : path[thirdPart] && path[thirdPart] === "collection"
      ? "collection"
      : path[thirdPart] !== "user" &&
        path[4 + INDEX_WITHOUT_L_PREFIX] &&
        path[5 + INDEX_WITHOUT_L_PREFIX]
      ? "supportQuestionPage"
      : path[4 + INDEX_WITHOUT_L_PREFIX] &&
        path[thirdPart] &&
        path[thirdPart] !== "error" &&
        path[thirdPart] !== "user"
      ? "supportQuestionsListPage"
      : "pagetext";
  };

  const setCurrentPage = (page) => {
    updateState((draft) => {
      draft.currentPage = page;
    });
  };

  const fetchAndSetBannersData = async (page, mobileCondition) => {
    if (isInIframe()) return;
    const response = await apiRequest(
      "Banners",
      "ReactGetRandomBannerForPage",
      {
        location: mobileCondition ? page + "-mobile" : page,
      },
      "POST"
    );
    if (response.status === "success") {
      updateState((draft) => {
        draft.bannersData = response.data;
      });
    }
  };

  const getBannerData = (position) => {
    // forbidden banners by request
    const forbiddenList = [13, 5];
    const relevantBannersList =
      state.bannersData?.filter(
        (banner) =>
          banner.BannerSourceUrl === position &&
          !forbiddenList.includes(banner.BannerId)
      ) || [];
    const relvantBanner =
      relevantBannersList.length > 1
        ? relevantBannersList[
            Math.floor(Math.random() * relevantBannersList.length)
          ]
        : relevantBannersList[0];
    return relvantBanner;
  };

  const fetchRecaptchaRequired = async () => {
    const response = await apiRequest(
      "IPBlocks",
      "ReactGetRecaptchaStatus",
      {},
      "GET"
    );
    if (response.status === "success") {
      updateState((draft) => {
        draft.recaptchaRequired = response.data;
      });
    }
  };

  const getRecaptchaRequired = () => {
    return state.recaptchaRequired;
  };

  const fetchAnnouncement = async (page, userId) => {
    if (isInIframe()) return;
    const response = await apiRequest(
      "Announcements",
      "GetAnnouncementForDisplay",
      {
        location: page,
        culture: getLanguage(),
      },
      "POST"
    );
    if (response.status === "success") {
      const nameToSave = `Announcement-${response.data?.CookieName}`;
      const isExistsCookie = cookies.get(nameToSave);
      if (!isExistsCookie) {
        updateState((draft) => {
          draft.announcement = response.data;
        });
      } else {
        resetAnnouncement();
      }
    }
  };

  const resetAnnouncement = () => {
    updateState((draft) => {
      draft.announcement = null;
    });
  };

  const closeAnnouncement = async () => {
    updateState((draft) => {
      draft.announcement = null;
    });
  };

  /**
   * gets the path taking into account the current language
   * @param {(String|Object|Function)} [to = null] - if needed, the "to" prop to be given to a react-router Route
   * @returns {String} a concatenated path with the current language
   */
  const getPath = (to = null) => {
    if (to === null) return `/${state.lang}/`;

    // why would this happen?
    if (Array.isArray(to)) {
      console.warn(
        "ContentFactory.getPath received an array, this should not happen"
      );
      return `/${state.lang}/`;
    }

    switch (typeof to) {
      case "object":
        return concatPath(to);
      case "function":
        return (location) => concatPath(to(location));
      default:
        // case "string"
        return `/${state.lang}${to}`;
    }
  };

  /**
   * getting constText to show in the DOM
   * @param {String} - a key the request
   * @returns value of the requested constText key
   */
  const getTextByKey = (key, isFirstLetterUpperCase) => {
    // dev settings
    if (state.DEV_showConstKeys && isDev) {
      return key;
    }

    let constKey = key;
    let errorCode = "";

    //checking for structure of generic error ("something went wrong, error code: xxxx")
    if (key && key.includes(":")) {
      errorCode = key.split(":")[1];
      constKey = key.split(":")[0];
    }
    //checking from space char. (valid key should not contain spaces!)
    //if space is included, than the text was returned from the server instead of its key (mostly happen for form error)
    if (key && key.includes(" ")) {
      return key;
    }

    //return constkey value from state. if isFirstLetterUpperCase===true, make the first letter uppercase.
    return state.constText[constKey]
      ? state.constText[constKey].length > 0
        ? isFirstLetterUpperCase
          ? state.constText[constKey].charAt(0).toUpperCase() +
            state.constText[constKey].slice(1)
          : state.constText[constKey] + errorCode
        : "missing const: " + constKey
      : constKey;
  };

  /**
   * getting constText to show in the DOM as html
   * @param {String} - a key the request
   * @returns text parsed as html
   */
  const getHtmlByKey = (key) => {
    // dev settings
    if (state.DEV_showConstKeys && isDev) {
      return key;
    }

    return state.constText[key]
      ? state.constText[key].length > 0
        ? parse(state.constText[key].replace("#lang#", state.lang))
        : "missing const: " + key
      : key;
  };
  const upperCaseFirstLetter = (text) => {
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : null;
  };
  const getSupportText = () => state.supportText;

  /**
   * fetching SupportCategories from API and set it in state
   *@param boolean - isLanguageChange - will be true only if this function will be called as language-changing side-effect
   */
  const fetchSupportCategories = async (setIsSupportCategoriesReturned) => {
    //in case of language change and null supportCategories, no need to fetch support categories
    // if (isLanguageChange && !getSupportText().categories) {
    //   return;
    // }
    if (isInIframe()) {
      setIsSupportCategoriesReturned(true);
      return;
    }
    const response = await apiRequest("Contents", "GetAllSupportCategories", {
      culture: getLanguageCulture(),
      useCache: false,
    });
    if (response.status === "success") {
      updateState((draft) => {
        draft.supportText.categories = response.data;
      });
      setIsSupportCategoriesReturned(true);
    } else {
      logger(
        "ContentFactory",
        "fetchSupportCategories",
        `unable to fetch support categories`
      );
    }
  };

  /**
   * fetching SupportCategoryQuestions from API and set it in state //
   *@param boolean - isLanguageChange - will be true only if this function will be called as language-changing side-effect
   */
  const fetchSupportQuestionsList = async (
    category,
    setIsSupportCategoriesReturned
  ) => {
    if (isInIframe()) {
      setIsSupportCategoriesReturned(true);
      return;
    }
    if (category) {
      const response = await apiRequest(
        "Contents",
        "GetAllSupportCategoriesQuestionsByCategory",
        {
          culture: getLanguageCulture(),
          useCache: false,
          category: (category === "jumboembed"
            ? "jumbo-embed"
            : category === "receiving-and-downloading-files" &&
              getLanguageCulture() === "he-IL"
            ? "download"
            : category === "jumbopay"
            ? "aboutjambopay"
            : category.charAt(0).toLowerCase() + category.slice(1)
          ).replace(/-/g, "-"),
        }
      );

      if (response.status === "success") {
        updateState((draft) => {
          draft.supportText.questionsList = response.data;
        });
        setIsSupportCategoriesReturned(true);
      } else {
        logger(
          "ContentFactory",
          "fetchSupportQuestionsList",
          `unable to fetch support Support Questions List`
        );
      }
    } else {
      setIsSupportCategoriesReturned(true);
    }
  };

  const fetchFeaturesList = async () => {
    const response = await apiRequest("Contents", "GetAllServices", {
      culture: getLanguageCulture(),
      forHomePage: false,
      useCache: false,
    });

    if (response.status === "success") {
      updateState((draft) => {});
    } else {
      logger(
        "ContentFactory",
        "fetchSupportQuestionsList",
        `unable to fetch support Support Questions List`
      );
    }
  };

  /**
   * fetching SupportCategoryQuestions from API and set it in state
   *@param boolean - isLanguageChange - will be true only if this function will be called as language-changing side-effect
   */
  const fetchSupportSingleQuestion = async (
    category,
    setIsSupportQuestionReturned
  ) => {
    if (category) {
      const response = await apiRequest(
        "Contents",
        "GetSingleSupportCategoriesQuestionsByCategory",
        {
          culture: getLanguageCulture(),
          useCache: false,
          category: category,
        }
      );

      if (response.status === "success") {
        updateState((draft) => {
          draft.supportText.question = response.data;
        });
        setIsSupportQuestionReturned(true);
      } else {
        logger(
          "ContentFactory",
          "fetchSupportSingleQuestion",
          `unable to fetch support Support SingleQuestion`
        );
      }
    } else {
      setIsSupportQuestionReturned(true);
    }
  };

  //determine if the support questions is helpful
  const setSupportQuestionHelpful = async (
    question,
    isHelpful,
    UserPrevVote
  ) => {
    const response = await apiRequest(
      "Contents",
      "IsSupportCategoryQuestionHelpful",
      {
        Culture: getLanguageCulture(),
        IsHelpful: isHelpful,
        QuestionSystemName: question,
        UserPrevVote: "",
      },
      "POST"
    );
    if (response.status === "success") return true;
    else {
      logger(
        "ContentFactory",
        "setSupportQuestionHelpful",
        `unable to determine if Support Question Helpful`
      );
    }
  };

  /**
   * fetching data of global const-text
   */
  const fetchGlobalConsts = async (setIsGlobalConstsReturned) => {
    setIsGlobalConstsReturned(false);
    const response = await apiRequest("Translations", "ReactGetConsts", {
      lang: getLanguage(),
      suffix: "global",
    });

    if (response.status === "success") {
      const data = response.data;
      updateState((draft) => {
        draft.constText = { ...draft.constText, ...data };
      });
      setIsGlobalConstsReturned(true);
    }

    //if fail, log error for getting text.
    else {
      logger(
        "ContentFactory",
        "fetchConsts",
        `unable to fetch text in language [${getLanguage()}] to category: global`
      );
    }
  };

  const CONSTS_PROFILE = {
    "sub-users": "subusers",
    account: "account",
    details: "details",
    "account-settings": "NOTIFICATIONSSETTINGS",
    jumbocode: "jumbocode",
    payments: "payments",
    "form-management": ["formmanagement", "form"],
    "statuses-management": "statusesmanagement",
    "order-tracker": ["ordertrackerpage", "ordertracker"],
    ftp: "ftp",
    api: "api",
    integration: "integration",
    "business-profile": "businessprofile",
  };

  // fetchConsts categories data
  const CONSTS_CATEGORIES = {
    collection: "gallery",
    uploadspeedtest: "speed-test",
    user: "profileuser",
    uploadspeed: "speed-test",
    jumbopay: "aboutjumbopay",
    codes: "jumbocode-recovery",
    registrationcomplete: "registrationcomplete",
    page: "personalpage",
    pageedit: "personalpage",
    form: "form",
    ordertracker: "ordertracker",
    createform: "createform",
    supportnew: "support",
  };

  // fetchConsts categories where the global loader should not be trigerred
  const CATEGORIES_WITH_NESTED_ROUTES = ["profileuser", "support"];
  /**
   * fetching data of const-text for requested/available categories
   * @param {String} suffix -the category to fetch const data for.
   */
  const fetchConsts = async (setIsConstsReturned, suffix = null) => {
    let categoryToFetch = window.location.pathname
      .split("/")[2]
      ?.replace(/-/g, "");

    categoryToFetch = CONSTS_CATEGORIES.hasOwnProperty(categoryToFetch)
      ? CONSTS_CATEGORIES[categoryToFetch]
      : categoryToFetch;

    // triggers the global loader after the route change
    !CATEGORIES_WITH_NESTED_ROUTES.join(",").includes(categoryToFetch) &&
      setIsConstsReturned(false);

    const response = await apiRequest("Translations", "ReactGetConsts", {
      lang: getLanguage(),
      suffix: suffix || categoryToFetch,
    });

    if (response.status === "success") {
      setIsConstsReturned(true);
      const data = response.data;
      updateState((draft) => {
        draft.constText = { ...draft.constText, ...data };
      });
    } else {
      setIsConstsReturned(false);
      logger(
        "ContentFactory",
        "fetchConsts",
        `unable to fetch text in language [${getLanguage()}] to category: ${categoryToFetch}`
      );
    }

    if (categoryToFetch === "home" && !suffix) {
      fetchConsts(setIsConstsReturned, "branding");
      fetchConsts(setIsConstsReturned, "profileuser");
    } else {
      if (setIsConstsReturned) setIsConstsReturned(true);
    }

    if (["purchase"].includes(categoryToFetch) && !suffix) {
      fetchConsts(setIsConstsReturned, "plans");
    } else {
      if (setIsConstsReturned) setIsConstsReturned(true);
    }

    if (["inbox", "gallery"].includes(categoryToFetch) && !suffix) {
      fetchConsts(setIsConstsReturned, "galleryinbox");
    } else {
      if (setIsConstsReturned) setIsConstsReturned(true);
    }

    if (["personalpage"].includes(categoryToFetch) && !suffix) {
      fetchConsts(setIsConstsReturned, "createform");
    } else {
      if (setIsConstsReturned) setIsConstsReturned(true);
    }

    if (["createform"].includes(categoryToFetch) && !suffix) {
      fetchConsts(setIsConstsReturned, "dynamicform");
    } else {
      if (setIsConstsReturned) setIsConstsReturned(true);
    }

    if (["form"].includes(categoryToFetch) && !suffix) {
      fetchConsts(setIsConstsReturned, "upload");
    } else {
      if (setIsConstsReturned) setIsConstsReturned(true);
    }

    if (["ordertracker"].includes(categoryToFetch) && !suffix) {
      //fetchConsts(setIsConstsReturned, "personalpage");
    } else {
      if (setIsConstsReturned) setIsConstsReturned(true);
    }

    if (["profileuser"].includes(categoryToFetch) && !suffix) {
      //Get sub-users page consts
      const profilePage = window.location.pathname.split("/").at(-1);
      //check if profilePage is array
      fetchConsts(setIsConstsReturned, "branding");
      if (
        profilePage !== undefined &&
        CONSTS_PROFILE[profilePage] !== undefined
      ) {
        if (Array.isArray(profilePage)) {
          profilePage.forEach((page) => {
            fetchConsts(setIsConstsReturned, page);
          });
        } else {
          fetchConsts(setIsConstsReturned, CONSTS_PROFILE[profilePage]);
        }
      } else {
        fetchConsts(setIsConstsReturned, "profileuser");
      }
    } else {
      if (setIsConstsReturned) setIsConstsReturned(true);
    }
  };

  //get form and uploader consts by form language
  const fetchEmbedPageConsts = async ({ formLanguage }) => {
    const response = await apiRequest(
      "Translations",
      "ReactGetConstsEmbedPage",
      {
        lang: formLanguage,
      }
    );
    if (response.status === "success") {
      const data = response.data;
      updateState((draft) => {
        draft.constText = { ...draft.constText, ...data };
      });
    } else {
      logger(
        "ContentFactory",
        "fetchEmbedPageConsts",
        `unable to fetch text in language [${getLanguage()}]`
      );
    }
  };

  //get order tracker script consts by embedpage language (need this only for embed page edit)
  const fetchOrderTrackerConsts = async ({ language }) => {
    const response = await apiRequest(
      "Translations",
      "ReactGetConstsOrderTracker",
      {
        lang: language ? language : getLanguage(),
      }
    );
    if (response.status === "success") {
      const data = response.data;
      updateState((draft) => {
        draft.constText = { ...draft.constText, ...data };
      });
    } else {
      logger(
        "ContentFactory",
        "fetchOrderTrackerConsts",
        `unable to fetch text in language [${getLanguage()}]`
      );
    }
  };

  /**
   * fetching metadata from server and updating state.
   * @param {String} pageName - page to get data for.
   */
  const fetchAndSetMetaData = async (currentPage, successCallback) => {
    if (currentPage === "registration-complete") {
      currentPage = "incomplete";
    }
    if (currentPage === "pay-as-you-go" || currentPage === "codes") {
      currentPage = "jumbocode-recovery";
    }
    if (currentPage === "upload-speed-test") {
      currentPage = "speed-test";
    }
    if (currentPage === "registration") {
      currentPage = "registrationPage";
    }
    if (currentPage === "page" || currentPage === "page-edit") {
      currentPage = "personalpage";
    }

    if (currentPage && !isInIframe()) {
      // const categoryToFetch = currentPage.replace(/-/g, "");

      const response = await apiRequest("Translations", "ReactGetPageText", {
        culture: getLanguageCulture(),
        pageTextName: currentPage,
      });
      if (response.status === "success") {
        responseUpdater(response, setState, state);
        successCallback(true);
      } else {
        logger("ContentFactory", "fetchMetaData", response.data);
      }
    } else {
      successCallback(true);
    }
  };

  const setBlackFridayTime = (blackFridayCount) => {
    updateState((draft) => {
      draft.blackFridayTime = blackFridayCount;
    });
  };

  const getBlackFridayTime = () => {
    return state.blackFridayTime;
  };

  const setBlackFridayTimeCookie = (blackFridayTimeCookieBool) => {
    updateState((draft) => {
      draft.blackFridayTimeCookieBool = blackFridayTimeCookieBool;
    });
  };

  const getBlackFridayTimeCookie = () => {
    const showHeader =
      new Date().toLocaleDateString() !== cookies.get("BlackFridayModal");

    return (
      state.blackFridayTimeCookieBool !== false &&
      state.blackFridayTimeCookieBool !== undefined &&
      showHeader
    );
  };

  const setIsBlackFriday = (isBlackFriday) => {
    updateState((draft) => {
      draft.isBlackFriday = isBlackFriday;
    });
  };

  const setCouponCode = (couponCode) => {
    updateState((draft) => {
      draft.couponCode = couponCode;
    });
  };

  const getCouponCode = () => state.couponCode;

  const getIsBlackFriday = () => {
    return state.isBlackFriday;
  };

  const setCampaignCouponDiscount = (campaignCouponDiscount) => {
    updateState((draft) => {
      draft.campaignCouponDiscount = campaignCouponDiscount;
    });
  };

  const getCampaignCouponDiscount = () => {
    return state.campaignCouponDiscount;
  };

  const setCampaignCouponCode = (campaignCouponCode) => {
    updateState((draft) => {
      draft.campaignCouponCode = campaignCouponCode;
    });
  };

  const getCampaignCouponCode = () => {
    return state.campaignCouponCode;
  };

  const setCampaignCouponOnlyYearly = (campaignCouponOnlyYearly) => {
    updateState((draft) => {
      draft.campaignCouponOnlyYearly = campaignCouponOnlyYearly;
    });
  };

  const getCampaignCouponOnlyYearly = () => {
    return state.campaignCouponOnlyYearly;
  };

  const getArtist = () => {
    return state.artist;
  };

  const replaceDynamicCouponValueInText = (text) => {
    return text.replace("30", state.campaignCouponDiscount);
  };

  // dev settings

  const DEV_setShowConstKeys = (showConstKeys) => {
    if (isDev) {
      updateState((draft) => {
        draft.DEV_showConstKeys = showConstKeys;
      });
    }
  };

  const DEV_getShowConstKeys = () => {
    return state.DEV_showConstKeys;
  };

  //finally, return the API
  return {
    /*---- BASIC SETTINGS ----*/
    isBasicSettingsInitialized,
    getAppVersion,
    getBasicSettingsData,
    initBasicSettings,
    isGoogleAnalyticsInitialized,
    setIsGoogleAnalyticsInitialized,
    fetchAndSetBannersData,
    getBannerData,
    fetchRecaptchaRequired,
    getRecaptchaRequired,
    /*---- SUPPORT CENTER ----*/
    getSupportText,
    fetchSupportCategories,
    fetchSupportQuestionsList,
    fetchSupportSingleQuestion,
    setSupportQuestionHelpful,
    /*---- TEXT CATEGORIES  ----*/
    fetchConsts,
    fetchEmbedPageConsts,
    fetchOrderTrackerConsts,
    fetchGlobalConsts,
    getTextCategories,
    getAllTextData,
    getTextByKey,
    getHtmlByKey,
    replaceKeywordsInText,
    /*---- PAGE TEXT / METADATA----*/
    getPageText,
    getMetaData,
    fetchAndSetMetaData,
    setCurrentPage,
    getCurrentPage,
    getCurrentPageType,
    upperCaseFirstLetter,
    /*---- LANGUAGE----*/
    setLanguage,
    getLanguage,
    getPath,
    getLanguages,
    getDirection,
    getLanguageCulture,
    /*---- BACKGROUND ----*/
    getDefaultBackgroundImage,
    fetchDefaultBackgroundImage,
    fetchAdsBackground,
    setAdsBackground,
    getAdsBackground,
    fetchFeaturesList,
    getArtist,
    getRandBackgroundFileType,
    /*---- BLACK FRIDAY ----*/
    setBlackFridayTime,
    getBlackFridayTime,
    setBlackFridayTimeCookie,
    getBlackFridayTimeCookie,
    setCouponCode,
    getCouponCode,
    setIsBlackFriday,
    getIsBlackFriday,
    setCampaignCouponDiscount,
    getCampaignCouponDiscount,
    setCampaignCouponCode,
    getCampaignCouponCode,
    setCampaignCouponOnlyYearly,
    getCampaignCouponOnlyYearly,
    replaceDynamicCouponValueInText,
    /*---- ANNOUNCEMENT ----*/
    getAnnouncement,
    fetchAnnouncement,
    closeAnnouncement,
    resetAnnouncement,
    getRandBackgroundRedirectUrl,
    /*---- DEV SETTINGS ----*/
    DEV_setShowConstKeys,
    DEV_getShowConstKeys,
  };
}

export default ContentFactory;
